/**
 * Direct browser -> S3 uploader supporting both single PUT and multipart.
 * Uses XMLHttpRequest for upload progress (fetch lacks upload.onprogress).
 */

const DEFAULT_CONCURRENCY = 4
const DEFAULT_RETRIES = 3

function putBlob({ url, blob, signal, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          etag:
            xhr.getResponseHeader('ETag') ||
            xhr.getResponseHeader('etag') ||
            null,
          status: xhr.status,
        })
      } else {
        reject(new Error(`PUT failed: HTTP ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new Error('Network error during PUT'))
    xhr.onabort = () => reject(new Error('Upload aborted'))
    if (signal) {
      const onAbort = () => {
        try {
          xhr.abort()
        } catch {
          // ignore
        }
      }
      if (signal.aborted) onAbort()
      else signal.addEventListener('abort', onAbort, { once: true })
    }
    xhr.send(blob)
  })
}

async function putWithRetry(opts, retries = DEFAULT_RETRIES) {
  let lastErr
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await putBlob(opts)
    } catch (err) {
      lastErr = err
      if (opts.signal?.aborted) throw err
      const delay = 1000 * Math.pow(2, attempt) // 1s, 2s, 4s
      await new Promise((r) => setTimeout(r, delay))
    }
  }
  throw lastErr
}

/**
 * @param {Object} args
 * @param {Blob|File} args.file
 * @param {Object} args.initiateResponse  // from POST /api/gis/raster/initiate
 * @param {number} [args.concurrency=4]
 * @param {(pct: number) => void} [args.onProgress]
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<{mode: 'single'} | {mode: 'multipart', parts: Array<{part_number: number, etag: string}>}>}
 */
export async function uploadFileToS3({
  file,
  initiateResponse,
  concurrency = DEFAULT_CONCURRENCY,
  onProgress,
  signal,
}) {
  const totalSize = file.size
  const bytesDoneByChunk = new Map()
  function emit() {
    if (!onProgress) return
    let total = 0
    for (const v of bytesDoneByChunk.values()) total += v
    const pct = Math.min(100, Math.round((total / totalSize) * 100))
    onProgress(pct)
  }

  if (initiateResponse.mode === 'single') {
    bytesDoneByChunk.set(1, 0)
    await putWithRetry({
      url: initiateResponse.put_url,
      blob: file,
      signal,
      onProgress: (loaded) => {
        bytesDoneByChunk.set(1, loaded)
        emit()
      },
    })
    bytesDoneByChunk.set(1, totalSize)
    emit()
    return { mode: 'single' }
  }

  // Multipart
  const { chunk_size, presigned_part_urls } = initiateResponse
  const queue = presigned_part_urls.map((p, idx) => ({
    part_number: p.part_number,
    url: p.url,
    start: idx * chunk_size,
    end: Math.min((idx + 1) * chunk_size, totalSize),
  }))
  const completedParts = []

  async function worker() {
    while (queue.length) {
      const job = queue.shift()
      if (!job) break
      const blob = file.slice(job.start, job.end)
      bytesDoneByChunk.set(job.part_number, 0)
      const { etag } = await putWithRetry({
        url: job.url,
        blob,
        signal,
        onProgress: (loaded) => {
          bytesDoneByChunk.set(job.part_number, loaded)
          emit()
        },
      })
      bytesDoneByChunk.set(job.part_number, job.end - job.start)
      emit()
      completedParts.push({ part_number: job.part_number, etag })
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, presigned_part_urls.length) },
    () => worker(),
  )
  await Promise.all(workers)
  completedParts.sort((a, b) => a.part_number - b.part_number)
  return { mode: 'multipart', parts: completedParts }
}
